import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr, fileViewUrl } from "../lib/api";
import { Plus, MapPin, Calendar, Users, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function Awareness() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/awareness")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Mass Education</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Awareness Programs</h1>
          <p className="text-brand-muted mt-1 text-sm">Health camps, talks & community drives</p>
        </div>
        <Link
          to="/awareness/new"
          data-testid="add-awareness-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors"
        >
          <Plus className="h-4 w-4" /> New Program
        </Link>
      </div>

      {loading ? (
        <div className="text-brand-muted">Loading…</div>
      ) : items.length === 0 ? (
        <Empty />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((a) => (
            <Link to={`/awareness/${a.id}`} key={a.id} className="bg-white border border-brand-border rounded-lg overflow-hidden hover:border-brand-primary transition-colors" data-testid={`awareness-card-${a.id}`}>
              {a.photo_paths?.[0] ? (
                <img src={fileViewUrl(a.photo_paths[0])} alt={a.title} className="w-full h-44 object-cover" />
              ) : (
                <div className="w-full h-44 bg-brand-surfaceAlt grid place-items-center">
                  <Megaphone className="h-10 w-10 text-brand-muted" />
                </div>
              )}
              <div className="p-5">
                <div className="text-[11px] uppercase tracking-wider text-brand-muted font-semibold">{a.topic}</div>
                <h3 className="font-heading text-lg font-semibold mt-0.5">{a.title}</h3>
                {a.description && <p className="text-sm text-brand-muted mt-2 line-clamp-2">{a.description}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-brand-muted">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {a.village}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {a.program_date}</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {a.attendees} attendees</span>
                </div>
                {a.photo_paths?.length > 1 && (
                  <div className="mt-3 flex gap-1.5">
                    {a.photo_paths.slice(1, 5).map((p) => (
                      <img key={p} src={fileViewUrl(p)} alt="" className="h-12 w-12 object-cover rounded border border-brand-border" />
                    ))}
                  </div>
                )}
                {a.latitude && a.longitude && (
                  <a target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${a.latitude},${a.longitude}`} onClick={(e) => e.stopPropagation()} className="mt-3 inline-flex text-xs items-center gap-1 text-brand-primary">
                    <MapPin className="h-3.5 w-3.5" /> View on map
                  </a>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty() {
  return (
    <div className="rounded-lg border-2 border-dashed border-brand-border bg-white p-12 text-center">
      <div className="mx-auto h-14 w-14 rounded-full bg-brand-surfaceAlt grid place-items-center">
        <Megaphone className="h-7 w-7 text-brand-primary" />
      </div>
      <h3 className="font-heading text-xl font-semibold mt-4">No awareness programs yet</h3>
      <p className="text-brand-muted mt-2 text-sm max-w-md mx-auto">
        Log your first mass health education or awareness drive with photos and geo-location.
      </p>
      <Link to="/awareness/new" className="inline-flex mt-6 items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors" data-testid="empty-add-awareness-btn">
        <Plus className="h-4 w-4" /> Log Program
      </Link>
    </div>
  );
}
